<template>
  <div
    class="product-bundle-select product-bundle-select__wrapper"
    :disabled="isDisabled"
    :class="{
      'product-bundle-select__extra': isExtraOption,
    }"
  >
    <button
      type="button"
      class="product-bundle-select__container"
      @click="openOptionsOverlay(option)"
    >
      <div class="product-bundle-select__image-container">
        <div class="product-bundle-select__image-main">
          <responsive-image
            class="product-bundle-select__primary-image"
            :image="currentVariantImage"
            ratio="1:1"
            sizes="(min-width: 1024px) 178px, 120px"
            widths="178, 356, 534"
          />
        </div>
      </div>
      <div class="product-bundle-select__content text-left">
        <p
          class="product-bundle-select__title text-mobile-headlines-h6 text-desktop-headlines-h6"
          v-text="currentItemName"
        />
        <p class="product-card-select__variant">
          <icon-pencil-simple />

          <span
            class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-text="currentVariantTitle"
          />
        </p>
      </div>

      <template v-if="!isExtraOption">
        <icon-chevron-right />
      </template>
    </button>
  </div>

  <teleport to="body">
    <overlay
      v-if="!isExtraOption"
      :ref="`optionsModal-${option.handle}-${option.key}`"
      block-class="product-bundle-select-options"
      direction="up"
      :namespace="`optionsModal-${option.handle}-${option.key}`"
      type="shelf-modal"
    >
      <template #title>
        <span v-text="$string('product.form.bundle_modal_title')" />
      </template>
      <template #body>
        <div class="product-bundle-select-options__content">
          <div class="product-bundle-select-options__image-container">
            <responsive-image
              class="product-bundle-select-options__image"
              :image="selectedOption.featured_image"
              ratio="1:1"
              sizes="(min-width: 1024px) 178px, 120px"
              widths="178, 356, 534"
            />
          </div>

          <div class="product-bundle-select-options__title">
            {{ currentItemName }}
          </div>
        </div>

        <form
          class="product-bundle-select-options__form"
          @submit.prevent="handleUpdateSelectedVariant"
        >
          <legend
            class="product-options__option-title text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-text="option.extraBundleInfo?.type"
          />

          <div class="product-bundle-select__dropdown">
            <p
              v-if="loading"
              class="product-options__dropdown-placeholder text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            />

            <select
              v-model="selectedVariant"
              class="product-bundle-select__variant-select text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
              :aria-label="currentItemName"
              @change="handleChangedVariant"
            >
              <template v-for="value of dropdownValues" :key="value.key">
                <option
                  v-if="!value.hidden && !value.soldOut && value.available"
                  class="product-options__option text-body-s-regular text-body-m-regular-desktop"
                  :value="value.value"
                  v-text="value.value"
                />
              </template>

              <optgroup
                v-if="availableState.length > 0"
                :label="$string('product.form.sold_out')"
              >
                <template v-for="value of availableState" :key="value.key">
                  <option
                    v-if="!value.hidden"
                    class="product-options__option text-body-s-regular text-body-m-regular-desktop"
                    disabled
                    :value="value.value"
                    v-text="value.value"
                  />
                </template>
              </optgroup>
            </select>

            <div class="product-options__dropdown-trigger">
              <div
                class="product-options__option-count text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
                v-html="option.countName"
              />

              <icon-chevron-down />
            </div>
          </div>
          <div class="product-bundle-select-options__button-wrapper">
            <btn
              class="product-bundle-select-options__save"
              :label="$string('product.form.bundle_btn_txt_save')"
              type="submit"
              @click="closeOptionsOverlay(option)"
            />
          </div>
        </form>
      </template>
    </overlay>
  </teleport>
</template>

<script>
/**
 * Vue: Product bundle select (product-bundle-select)
 * -----------------------------------------------------------------------------
 * Select Bundle Products in a modal on the PDP.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */

import Btn from '~global/btn/btn';
import Overlay from '~global/overlay/overlay';
import ResponsiveImage from '~global/images/responsive-image';
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg';
import IconPencilSimple from '~icons/general/pencil-simple.svg';

import { computeDiscountLogic, emitUpdateVariant } from '~/helpers/vue';
import { mapActions } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import { convertToHandle } from '~/helpers/convert';

export default {
  name: 'ProductBundleSelect',

  components: {
    Btn,
    IconChevronDown,
    IconChevronRight,
    IconPencilSimple,
    Overlay,
    ResponsiveImage,
  },

  props: {
    option: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
    isExtraOption: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-bundle-product': emitUpdateVariant,
    'update-disabled-status': (payload) => {
      return typeof payload === 'boolean';
    },
  },

  data() {
    return {
      currentVariant: {},
      selectedVariant: {},
      selectedOption: {},
      isDisabled: false,
    };
  },

  computed: {
    /**
     * Compute sold out values.
     * @returns {Array}
     */
    availableState() {
      const soldOutValues = [];

      this.option.availableState.forEach((value) => {
        const bundleValue = this.option.bundle.find(
          (bundleItem) => bundleItem.id === value.id,
        );

        if (value.availableState !== 'available') {
          soldOutValues.push({
            ...value,
            value: bundleValue.option1,
          });
        }
      });

      return soldOutValues;
    },

    /**
     * Compute dropdown values for popup.
     * @returns {Array}
     */
    dropdownValues() {
      const values = [];

      this.option.bundle.forEach((bundleItem) => {
        const soldOutVariant = this.option.availableState.find(
          (value) => value.id === bundleItem.id,
        );

        values.push({
          downcase: bundleItem.option1.toLowerCase(),
          featuredImage: bundleItem.featured_image,
          handle: this.option.handle,
          hidden: soldOutVariant?.availableState === 'hidden' || false,
          key: this.option.handle + bundleItem.option1,
          portions: bundleItem.portions,
          soldOut:
            soldOutVariant?.availableState === 'sold-out' ||
            soldOutVariant?.availableState === 'back-soon' ||
            false,
          value: bundleItem.option1,
          available: bundleItem.available,
        });
      });

      return values;
    },

    /**
     * Compute url for product chosen in modal
     * @returns {String}
     */
    modalProductUrl() {
      const handle = convertToHandle(this.option.extraBundleInfo.name);
      return `/products/${handle}?variant=${this.selectedOption.id}`;
    },

    /**
     * compute name of bundle item
     * @returns {String}
     */
    currentItemName() {
      if (this.option.extraBundleInfo?.name) {
        return this.option.extraBundleInfo.name;
      }
      return this.option.name;
    },

    /**
     * Compute current variant title.
     * @returns {String}
     */
    currentVariantTitle() {
      if (this.isDisabled && !this.isExtraOption) {
        return (
          this.option.currentOption.title.replaceAll(' / ', ', ') ||
          this.option.labels[0]
        );
      }
      return this.currentVariant?.title?.replaceAll(' / ', ', ') ===
        'Default Title'
        ? ''
        : this.currentVariant?.title?.replaceAll(' / ', ', ');
    },

    /**
     * Compute current variant Image.
     * @returns {Object}
     */
    currentVariantImage() {
      if (this.isDisabled && !this.isExtraOption) {
        return this.option.bundle[0].featured_media.preview_image;
      }

      if (this.selectedOption && this.selectedOption.featured_image) {
        return this.selectedOption.featured_image;
      }

      if (this.product.media && this.product.media.length > 0) {
        return this.product.media[0].image;
      }

      return this.product.featuredImage;
    },
  },

  watch: {
    /**
     * Watch current variant to update form's selected variant.
     */
    currentVariant() {
      this.selectedOption = this.currentVariant;

      this.soldOut = this.selectedOption?.available
        ? (this.soldOut = false)
        : (this.soldOut = true);

      this.$emit(
        'update-bundle-product',
        this.currentVariant,
        this.option.index,
      );
    },

    /**
     * Watch variant to update current variant.
     */
    currentOption() {
      this.currentOption = this.selectedOption;
    },
  },

  mounted() {
    let availableVariant = '';

    if (this.isExtraOption) {
      availableVariant =
        this.option.availableState === 'available' ? this.option : [];
    } else {
      availableVariant = this.option.availableState.filter(
        (state) => state.availableState === 'available',
      );
    }

    if (availableVariant.length > 0) {
      this.currentVariant = this.isExtraOption
        ? this.option?.currentOption
        : this.option.bundle.find(
            (bundleItem) => bundleItem.id === availableVariant[0].id,
          );
    } else {
      this.currentVariant = this.option.currentOption;
    }

    this.selectedOption = this.currentVariant;
    this.selectedVariant = this.currentVariant?.option1;
    this.soldOut = Boolean(!this.currentVariant?.available);

    this.isDisabled = this.isExtraOption
      ? this.option.availableState !== 'available'
      : !this.option.availableState.some(
          (state) => state.availableState === 'available',
        );
    /** emit the status to the main product to influence add to cart button */
    this.$emit('update-disabled-status', this.isDisabled);

    this.$emit('update-bundle-product', this.currentVariant, this.option.index);
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    /**
     * Open product options overlay.
     * If extra option and therefore static, do not open overlay.
     * @param {Object} option - Option object.
     */
    openOptionsOverlay(option) {
      if (this.isExtraOption) {
        return;
      }
      const namespace = `optionsModal-${option.handle}-${option.key}`;

      this.openOverlay({
        ignoreDismissed: true,
        namespace,
      });
    },

    /**
     * Close product options overlay.
     * @param {Object} option - Option object.
     */
    closeOptionsOverlay(option) {
      const namespace = `optionsModal-${option.handle}-${option.key}`;

      this.closeOverlay({
        ignoreDismissed: true,
        namespace,
      });
    },

    /**
     * Get discount logic.
     * @param {Boolean|Object} variant - Variant object to use.
     * @returns {Object}
     */
    getDiscountLogic(variant) {
      return computeDiscountLogic({
        discountPreview: false,
        onSale: variant?.compareAtPriceDiscount,
        product: this.product,
        variant,
      });
    },

    /**
     * Handle save updated variant.
     */
    handleUpdateSelectedVariant() {
      this.currentVariant = this.option.bundle.find(
        (bundleItem) => bundleItem.option1 === this.selectedVariant,
      );

      this.closeOptionsOverlay(this.currentVariant);
    },

    /**
     * Handle changed variant.
     */
    handleChangedVariant() {
      this.selectedOption = this.option.bundle.find(
        (bundleItem) => bundleItem.option1 === this.selectedVariant,
      );
    },
  },
};
</script>

<style lang="scss">
@import './product-bundle-select';
</style>
